import { Command, EditorState, Selection, TextSelection, Transaction } from "@tiptap/pm/state";

import { BracketFieldRange } from "../utils/find-bracket-fields";

import {
  DotPhraseField,
  isDotPhraseFieldChanged,
  readDotPhraseFields,
} from "./dot-phrase-fields-state";

export function findNextDotPhraseField(
  fields: BracketFieldRange[],
  position: number,
): BracketFieldRange | undefined {
  return fields.find((field) => field.from >= position) ?? fields[0];
}

export function findPreviousDotPhraseField(
  fields: BracketFieldRange[],
  position: number,
): BracketFieldRange | undefined {
  return fields.findLast((field) => field.to <= position) ?? fields.at(-1);
}

export function findDotPhraseFieldAt<Field extends BracketFieldRange>(
  fields: Field[],
  from: number,
  to: number,
): Field | undefined {
  return fields.find((field) => from > field.from && to < field.to);
}

function selectFieldValue(tr: Transaction, field: BracketFieldRange): Transaction {
  const from = tr.mapping.map(field.from, 1);
  const to = tr.mapping.map(field.to, -1);

  return tr.setSelection(TextSelection.create(tr.doc, from + 1, to - 1));
}

type FieldFinder = (
  fields: BracketFieldRange[],
  selection: Selection,
) => BracketFieldRange | undefined;

const findNextFromSelection: FieldFinder = (fields, selection) =>
  findNextDotPhraseField(fields, selection.to);

const findPreviousFromSelection: FieldFinder = (fields, selection) =>
  findPreviousDotPhraseField(fields, selection.from);

/**
 * Removes the brackets of `current` in one transaction, then selects the value of the field
 * `findTarget` picks from the rest, or collapses the caret after the value when none is left.
 */
function acceptField(
  state: EditorState,
  fields: DotPhraseField[],
  current: DotPhraseField,
  findTarget: FieldFinder,
): Transaction {
  const target = findTarget(
    fields.filter((field) => field !== current),
    state.selection,
  );
  const tr = state.tr.delete(current.to - 1, current.to).delete(current.from, current.from + 1);

  if (target) return selectFieldValue(tr, target);

  return tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(current.to, -1)));
}

function moveToField(findTarget: FieldFinder): Command {
  return (state, dispatch) => {
    const fields = readDotPhraseFields(state);
    const { from, to } = state.selection;
    const current = findDotPhraseFieldAt(fields, from, to);

    if (current && isDotPhraseFieldChanged(state.doc, current)) {
      dispatch?.(acceptField(state, fields, current, findTarget));

      return true;
    }

    const target = findTarget(fields, state.selection);

    if (!target) return false;

    dispatch?.(selectFieldValue(state.tr, target));

    return true;
  };
}

const acceptDotPhraseField: Command = (state, dispatch) => {
  const fields = readDotPhraseFields(state);
  const { from, to } = state.selection;
  const current = findDotPhraseFieldAt(fields, from, to);

  if (!current) return false;

  dispatch?.(acceptField(state, fields, current, findNextFromSelection));

  return true;
};

export const DOT_PHRASE_FIELD_COMMANDS = {
  next: moveToField(findNextFromSelection),
  previous: moveToField(findPreviousFromSelection),
  accept: acceptDotPhraseField,
} as const;
